// Phase 6 — Findings enrichment for telecom_v2
// Presentation-only helpers that turn the highest-priority structured findings
// into compact executive narrative blocks. The underlying findings table and
// data model are NOT modified.

export interface EnrichmentInput {
  id?: string;
  finding_title?: string | null;
  control_code?: string | null;
  domain?: string | null;
  risk_level?: string | null;
  priority_level?: string | null;
  recommended_action?: string | null;
  recommended_measures?: string | null;
  remediation_path?: string | null;
  timeframe?: string | null;
}

export interface EnrichedFinding {
  index: number;
  title: string;
  control: string;
  domain: string;
  risk: string;
  priority: string;
  whyItMatters: string;
  recommendedAction: string;
  supportingMeasures: string;
  timeHorizon: string;
}

export const ENRICHMENT_CAP = 8;

const CRITICAL = new Set(["Critical", "CRITICAL"]);
const HIGH = new Set(["High", "HIGH"]);

const RISK_RANK: Record<string, number> = {
  Critical: 1, CRITICAL: 1,
  High: 2, HIGH: 2,
  Moderate: 3, MODERATE: 3,
  Low: 4, LOW: 4,
};

const PRIORITY_RANK: Record<string, number> = {
  Immediate: 1, "Quick Win": 2, Strategic: 3,
};

function isTopPriority(f: EnrichmentInput): boolean {
  if (f.risk_level && (CRITICAL.has(f.risk_level) || HIGH.has(f.risk_level))) return true;
  if (f.priority_level === "Immediate") return true;
  return false;
}

/**
 * Selection rule:
 *   1. Include findings with risk_level Critical or High,
 *      OR priority_level "Immediate".
 *   2. Sort by risk severity, then priority, then control code.
 *   3. Cap at ENRICHMENT_CAP (8) — caller should surface the cap notice.
 */
export function selectTopFindings(
  rows: EnrichmentInput[],
  cap: number = ENRICHMENT_CAP
): { selected: EnrichmentInput[]; totalEligible: number; capped: boolean } {
  const eligible = rows.filter(isTopPriority);
  const sorted = [...eligible].sort((a, b) => {
    const ra = RISK_RANK[a.risk_level || ""] ?? 99;
    const rb = RISK_RANK[b.risk_level || ""] ?? 99;
    if (ra !== rb) return ra - rb;
    const pa = PRIORITY_RANK[a.priority_level || ""] ?? 99;
    const pb = PRIORITY_RANK[b.priority_level || ""] ?? 99;
    if (pa !== pb) return pa - pb;
    return (a.control_code || "").localeCompare(b.control_code || "");
  });
  const selected = sorted.slice(0, cap);
  return { selected, totalEligible: eligible.length, capped: eligible.length > cap };
}

/**
 * Derive a careful "Why it matters" sentence using only existing fields.
 * Never invents facts — composes from risk, priority, domain, and remediation
 * path / measures already authored by the reviewer.
 */
function deriveWhyItMatters(f: EnrichmentInput): string {
  const risk = (f.risk_level || "").toString();
  const priority = (f.priority_level || "").toString();
  const domain = f.domain || "this capability area";
  const control = f.control_code ? ` (${f.control_code})` : "";

  let severity = "an elevated risk exposure";
  if (CRITICAL.has(risk)) severity = "a critical residual risk to telecom operations";
  else if (HIGH.has(risk)) severity = "a high residual risk that warrants prompt action";
  else if (priority === "Immediate") severity = "an immediate-priority gap requiring action";

  const lensBits: string[] = [];
  if (priority === "Immediate") lensBits.push("flagged for immediate attention");
  else if (priority === "Quick Win") lensBits.push("addressable as a quick win");
  else if (priority === "Strategic") lensBits.push("part of the strategic remediation horizon");

  const lens = lensBits.length ? `, ${lensBits.join(" and ")}` : "";
  return `Weakness in ${domain}${control} represents ${severity}${lens}. Closing this gap reduces likelihood and impact of incidents tied to this control area.`;
}

export function enrichFinding(f: EnrichmentInput, index: number): EnrichedFinding {
  return {
    index,
    title: (f.finding_title || "Untitled finding").trim(),
    control: (f.control_code || "—").trim(),
    domain: (f.domain || "—").trim(),
    risk: (f.risk_level || "—").trim(),
    priority: (f.priority_level || "—").trim(),
    whyItMatters: deriveWhyItMatters(f),
    recommendedAction: (f.recommended_action || "—").trim(),
    supportingMeasures: (f.recommended_measures || f.remediation_path || "—").trim(),
    timeHorizon: (f.timeframe || "—").trim(),
  };
}

export function buildEnrichedFindings(
  rows: EnrichmentInput[],
  cap: number = ENRICHMENT_CAP
): { items: EnrichedFinding[]; totalEligible: number; capped: boolean; cap: number } {
  const { selected, totalEligible, capped } = selectTopFindings(rows, cap);
  return {
    items: selected.map((f, i) => enrichFinding(f, i + 1)),
    totalEligible,
    capped,
    cap,
  };
}
